import { APITypesV1 } from "@cur8/api-client";
import {
  Button,
  Icon24Alert,
  Icon24ArrowRight,
  Typography,
} from "@cur8/maneki";
import { useFinishCheckInMutation } from "hooks/mutations/useFinishCheckInMutation";
import { useFetchEndCheckInJobQuery } from "hooks/queries/useFetchEndCheckInJobQuery";
import { useLatestTimelineEventQuery } from "hooks/queries/useLatestTimelineEventQuery";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useRoomAssignedPatientQuery } from "hooks/queries/useRoomAssignedPatientQuery";
import { useRoomQuery } from "hooks/queries/useRoomQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { CheckInParams } from "lib/types/checkInParameters";
import { useEffect, useMemo, useState } from "react";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { useReporting } from "render/hooks/useReporting";
import { getScanFlowUrl } from "render/routes/getScanFlowUrl";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { CheckCard } from "render/ui/presentation/CheckCard";
import { Group } from "render/ui/presentation/Group";
import styles from "./styles.module.sass";

type AssignToRoomProps = {
  goTo(url: string): void;
} & CheckInParams;

export function AssignToRoomView({
  goTo,
  patientId,
  visitId,
}: AssignToRoomProps) {
  const { data: patient } = usePatientQuery({ patientId });
  const { data: visit } = useVisitQuery({ patientId, visitId });
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [showScanWarning, setShowScanWarning] = useState(false);
  const [roomConfirmed, setRoomConfirmed] = useState(false);

  const { data: featureFlags } = useFeaturePreference();

  const { handleError } = useReporting();

  const roomQuery = useRoomQuery({ roomId: visit?.roomId });
  const roomName = roomQuery.data?.displayName;

  const assignedPatientQuery = useRoomAssignedPatientQuery({
    roomId: visit?.roomId,
  });
  const occupant = assignedPatientQuery.data;
  const occupantIsOtherMember =
    occupant?.patientId != null && occupant.patientId !== patientId;

  const occupantTimelineQuery = useLatestTimelineEventQuery({
    patientId: occupant?.patientId,
    visitId: occupant?.visitId,
    options: {
      enabled:
        featureFlags.roomScanWarningEnabled &&
        occupantIsOtherMember &&
        occupant?.visitId != null,
    },
  });
  // Warn only when the feature flag is on, another member is assigned to this
  // room, and their visit is in a confirmed "scan" stage. Loading, errors, and
  // missing timelines (404) must never block a normal check-in.
  const otherMemberInScan =
    featureFlags.roomScanWarningEnabled &&
    occupantIsOtherMember &&
    occupantTimelineQuery.data?.stage === APITypesV1.TimelineStage.Scan;

  const finishCheckIn = useFinishCheckInMutation();
  const fetchEndCheckInJobQuery = useFetchEndCheckInJobQuery({
    jobId,
    patientId: patientId,
    visitId: visitId,
    options: {
      enabled: Boolean(jobId),
      refetchInterval: 1000,
    },
  });

  const isBodyScan = visit?.medicalExams?.includes(
    APITypesV1.MedicalExam.NekoBodyScan
  );
  const nextUrl = useMemo(() => {
    return getScanFlowUrl({
      patientId,
      visitId,
      medicalExams: visit?.medicalExams,
    });
  }, [visit?.medicalExams, patientId, visitId]);

  useEffect(() => {
    if (fetchEndCheckInJobQuery.data?.state === APITypesV1.JobState.Complete) {
      goTo(nextUrl);
      setJobId(undefined);
    }
    if (fetchEndCheckInJobQuery.data?.state === APITypesV1.JobState.Failed) {
      handleError("Assign to room job failed");
      setJobId(undefined);
    }
    if (fetchEndCheckInJobQuery.error) {
      handleError(fetchEndCheckInJobQuery.error);
    }
  }, [
    fetchEndCheckInJobQuery.data?.state,
    fetchEndCheckInJobQuery.error,
    goTo,
    handleError,
    nextUrl,
  ]);

  const memberName =
    patient?.name?.displayName || patient?.name?.firstName || "the member";

  const buttonText = isBodyScan
    ? `Assign ${memberName} to ${roomName ?? "room"}`
    : "Begin follow-up exams";

  const startFinishCheckIn = () => {
    if (finishCheckIn.isPending) {
      return;
    }
    finishCheckIn.mutate(
      { patientId, visitId },
      {
        onSuccess: (data) => setJobId(data.jobId),
      }
    );
  };

  if (showScanWarning) {
    return (
      <PageFrameContent>
        <InformationLayout
          content={
            <div className={styles.warning}>
              <div className={styles.warningIcon}>
                <Icon24Alert />
              </div>
              <Typography variant="display-s">
                A member is still performing scans in this room
              </Typography>
              <Typography variant="body-m" color="subtle">
                Are you sure you want to assign the room to another member?
              </Typography>
            </div>
          }
          buttons={
            <ActionsFooter
              left={
                <Button
                  variant="secondary"
                  onPress={() => setShowScanWarning(false)}
                  fillContainer
                >
                  Cancel
                </Button>
              }
              right={
                <Button
                  kind="destructive"
                  state={
                    finishCheckIn.isPending || Boolean(jobId)
                      ? "loading"
                      : undefined
                  }
                  onPress={startFinishCheckIn}
                  fillContainer
                  icon={<Icon24ArrowRight />}
                >
                  Yes, assign anyway
                </Button>
              }
            />
          }
        />
      </PageFrameContent>
    );
  }

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <>
            <div>
              <Typography variant="display-s">Confirm room</Typography>
              <Typography variant="body-m" color="subtle">
                Confirm you are assigning the member to the correct room.
              </Typography>
            </div>
            <Group highlight={roomConfirmed ? "blue" : "none"}>
              <CheckCard
                aria-label="Confirm room"
                isSelected={roomConfirmed}
                onChange={setRoomConfirmed}
              >
                <div>
                  <Typography variant="eyebrow-m" color="subtle">
                    Room
                  </Typography>
                  <Typography variant="title-s">{roomName}</Typography>
                </div>
              </CheckCard>
            </Group>
          </>
        }
        buttons={
          <ActionsFooter
            right={
              <Button
                state={
                  finishCheckIn.isPending || Boolean(jobId)
                    ? "loading"
                    : !roomConfirmed
                      ? "disabled"
                      : undefined
                }
                onPress={() => {
                  if (otherMemberInScan) {
                    setShowScanWarning(true);
                    return;
                  }
                  startFinishCheckIn();
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                {buttonText}
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
